/**
 * Vocabulary and timings for the "did you like this palette?" prompt.
 *
 * The ratings this collects are written into the **Prefetch** Supabase project's
 * `request_feedback` table — the same table prefetch.io's playground writes to
 * (see server/api/feedback.post.ts for why that is the right home). That makes
 * these slugs a contract with a database in another project:
 *
 *   • the `reason` CHECK constraint in the Prefetch repo's
 *     supabase/migrations/20260817000000_request_feedback.sql
 *   • FEEDBACK_REASON_SLUGS in that repo's server/schemas/feedback.ts
 *   • any dashboard already grouping on these values
 *
 * The slugs are therefore copied verbatim from Prefetch and must not be renamed
 * here — a new slug is rejected by the CHECK constraint at write time. Only the
 * *labels* are ours: Prefetch is rating a JSON API response, Colorize is rating
 * a colour palette, so the same six buckets are worded for what a visitor here
 * actually sees.
 *
 * Kept to six on purpose: the bar is one line tall, and every extra option is
 * one more reason to close it instead of answering.
 */
export interface FeedbackReason {
  slug: string
  label: string
}

export const FEEDBACK_REASONS: readonly FeedbackReason[] = [
  // The palette is wrong for the site — colours that appear nowhere on it.
  { slug: 'wrong_results', label: 'Wrong colors' },
  // Sections came back empty that the visitor expected to be filled. Points at
  // coverage (the crawl found nothing) rather than at extraction accuracy.
  { slug: 'missing_data', label: 'Missing colors' },
  // Right colours, unusable rendition: a broken favicon, a blank or stale
  // screenshot, muddy shades.
  { slug: 'low_quality', label: 'Bad screenshot or logo' },
  // The extraction worked but not the way the visitor assumed it would — a
  // naming and expectation-setting signal rather than a data one.
  { slug: 'not_expected', label: 'Not what I expected' },
  // Works fine, but doesn't give them what they came for (a different export,
  // a different grouping, more shades). This is the roadmap bucket.
  { slug: 'need_different_data', label: 'Need something else' },
  // Escape hatch. Selecting it is a strong hint to actually write the note.
  { slug: 'other', label: 'Something else' },
] as const

/** Max characters accepted in the optional free-text note (mirrors the DB CHECK). */
export const FEEDBACK_NOTE_MAX = 500

/** How long the "thanks" confirmation stays up before the bar retires itself (ms). */
export const FEEDBACK_THANKS_MS = 2200

/**
 * Delay between a palette rendering and the bar sliding in (ms).
 *
 * Longer than Prefetch's 900ms: there the result is a JSON panel that is done
 * when it paints, while here the palette lands before its screenshot and
 * favicon do. Asking "happy with this?" over a half-loaded result gets an
 * answer about the loading, not about the colours.
 */
export const FEEDBACK_PROMPT_DELAY_MS = 1800
